import { SimpleTool } from "@/components/workbook/SimpleTool";
import { Stat } from "@/components/tools/fields";

const T = (n, title, blurb) => ({ n, title, blurb });
const STATUS = ["Not started", "In progress", "Complete", "Blocked"];
const tx = (k, label, rows = 3) => ({ k, label, rows });

const pct = (a, b) => (Number(b) > 0 ? `${Math.round((Number(a || 0) / Number(b)) * 100)}%` : "—");

export const RegisterReview = ({ n }) => (
  <SimpleTool id="wb-register" storageKey="wb-register-v1" tool={T(n, "SEND Register Health Review", "Check the register is accurate, current and supported by evidence.")}
    groups={[{ letter: "A", title: "Register numbers", hint: "Numbers only. No pupil details.", cols: "sm:grid-cols-2 lg:grid-cols-4",
      fields: [{ k: "total", label: "Total pupils on roll", type: "number" }, { k: "register", label: "Pupils on the SEND register", type: "number" },
        { k: "ehcp", label: "Pupils with an EHCP", type: "number" }, { k: "support", label: "Pupils at SEN Support", type: "number" }] },
      { letter: "B", title: "Reflection", cols: "", fields: [tx("accurate", "Is the register accurate?"), tx("evidence", "What evidence supports this?"), tx("actions", "What actions are required?")] }]}>
    {(d) => (
      <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-3" aria-live="polite">
        <Stat label="On the SEND register" value={pct(d.register, d.total)} testid="wb-register-pct" />
        <Stat label="EHCP" value={pct(d.ehcp, d.total)} testid="wb-ehcp-pct" />
        <Stat label="SEN Support" value={pct(d.support, d.total)} testid="wb-support-pct" />
      </div>
    )}
  </SimpleTool>
);

const AREAS = [["attendance", "Attendance"], ["persistent", "Persistent absence"], ["inclusion", "Inclusion"], ["participation", "Participation"], ["barriers", "Barriers"]];

export const AttendanceReview = ({ n }) => (
  <SimpleTool id="wb-attendance" storageKey="wb-attendance-v1" tool={T(n, "Attendance & Inclusion Review", "Review attendance, persistent absence, inclusion, participation and barriers for pupils with SEND.")}
    groups={AREAS.map(([k, t], i) => ({ letter: String.fromCharCode(65 + i), title: t,
      fields: [tx(`${k}-now`, "What we know now (whole-school or group data only)"), tx(`${k}-next`, "What we will do next")] }))}
    tables={[{ name: "plan", letter: "F", title: "Development planning", cols: [{ k: "priority", label: "Priority" }, { k: "action", label: "Action" }, { k: "lead", label: "Lead" }, { k: "review", label: "Review date", type: "date" }], addLabel: "Add a priority" }]} />
);

export const ActionTracker = ({ n }) => (
  <SimpleTool id="wb-tracker" storageKey="wb-tracker-v1" tool={T(n, "Action Tracker", "Track every action with an owner, status, evidence and review date.")}
    tables={[{ name: "actions", rows: 4, title: "Actions", grid: "sm:grid-cols-2 lg:grid-cols-3",
      cols: [{ k: "action", label: "Action" }, { k: "owner", label: "Owner" }, { k: "status", label: "Status", type: "select", options: STATUS },
        { k: "evidence", label: "Evidence" }, { k: "review", label: "Review date", type: "date" }, { k: "notes", label: "Progress notes" }], addLabel: "Add an action" }]} />
);

export const ImprovementJournal = ({ n }) => (
  <SimpleTool id="wb-journal" storageKey="wb-journal-v1" tool={T(n, "SEND Improvement Journal", "Space to think, question and plan. Use it after meetings, reviews and training.")}
    groups={[{ title: "Journal", cols: "", fields: [tx("reflections", "Key reflections", 5), tx("questions", "Questions", 4), tx("ideas", "Ideas", 4), tx("next", "Next steps", 4), tx("discussion", "Discussion points", 4)] }]} />
);

export const SprintPlanner = ({ n }) => (
  <SimpleTool id="wb-sprint" storageKey="wb-sprint-v1" tool={T(n, "30-Day SEND Sprint Planner", "One focused improvement, four weeks of actions, one review.")}
    groups={[
      { letter: "A", title: "The sprint", cols: "md:grid-cols-3", fields: [tx("focus", "Sprint focus", 2), tx("challenge", "Challenge", 2), tx("impact", "Intended impact", 2)] },
      { letter: "B", title: "Weekly actions", fields: [1, 2, 3, 4].map((w) => tx(`week${w}`, `Week ${w} actions`)) },
      { letter: "C", title: "Sprint review", fields: [tx("measures", "Success measures"), tx("review", "Review notes")] },
    ]} />
);

export const NinetyDayPlanner = ({ n }) => (
  <SimpleTool id="wb-ninety" storageKey="wb-ninety-v1" tool={T(n, "90-Day SEND Improvement Planner", "A strategic priority broken into three 30-day phases, with evidence and evaluation.")}
    groups={[
      { letter: "A", title: "Priority", cols: "md:grid-cols-3", fields: [tx("priority", "Strategic priority", 2), tx("current", "Current position", 2), tx("target", "Target position", 2)] },
      { letter: "B", title: "Milestones", cols: "md:grid-cols-3", fields: [tx("p1", "First 30 days", 5), tx("p2", "Days 31 to 60", 5), tx("p3", "Days 61 to 90", 5)] },
      { letter: "C", title: "Evaluation", cols: "md:grid-cols-3", fields: [tx("evidence", "Evidence of success"), tx("evaluation", "Final evaluation"), tx("next", "Next priority")] },
    ]} />
);

const SOURCES = ["Ofsted findings", "Area SEND findings", "Internal audit", "Trust review", "Parent feedback"];

export const InspectionToAction = ({ n }) => (
  <SimpleTool id="wb-inspection" storageKey="wb-inspection-v1" tool={T(n, "Inspection Findings to Action Planning", "Turn findings from inspections, audits, reviews and parent feedback into clear, measurable actions.")}
    tables={[{ name: "findings", rows: 2, title: "Findings", hint: "One row per finding.", grid: "sm:grid-cols-2 lg:grid-cols-4",
      cols: [{ k: "source", label: "Source", type: "select", options: SOURCES }, { k: "finding", label: "Finding" }, { k: "impact", label: "Impact" },
        { k: "outcome", label: "Desired outcome" }, { k: "actions", label: "Actions" }, { k: "evidence", label: "Evidence required" },
        { k: "measures", label: "Success measures" }, { k: "review", label: "Review date", type: "date" }], addLabel: "Add a finding" }]}>
    {() => <WorkedExample />}
  </SimpleTool>
);

const EXAMPLE = [
  ["Finding", "Example only: \"Pupils with SEND do not consistently receive the adaptations set out in their support plans.\""],
  ["Improvement priority", "Every pupil with SEND receives agreed adaptations in every lesson."],
  ["30-day sprint", "Week 1: share one-page profiles with all teachers. Week 2: model three adaptations in briefing. Week 3: learning walks focused on adaptations. Week 4: review findings with staff."],
  ["90-day plan", "Days 1–30: the sprint above. Days 31–60: coaching for teachers where gaps were seen. Days 61–90: repeat learning walks and gather pupil and parent views."],
  ["Success measures", "Adaptations seen in most observed lessons; teacher confidence rises in the staff survey; parents report better consistency."],
  ["Review structure", "Fortnightly SENDCO check-in, half-termly report to the senior team, termly update to governors."],
];

const WorkedExample = () => (
  <div className="avoid-break mt-10 rounded-2xl border-2 border-dashed border-brand p-5 sm:p-6" data-testid="wb-worked-example">
    <p className="eyebrow mb-2">Worked example</p>
    <h4 className="font-display text-2xl font-medium">From inspection finding to review</h4>
    <ol className="mt-4 space-y-3">
      {EXAMPLE.map(([k, v], i) => (
        <li key={k} className="grid gap-1 sm:grid-cols-[200px_minmax(0,1fr)]">
          <span className="font-bold"><span className="font-display italic text-brand">{i + 1}.</span> {k}</span>
          <span className="text-ink-soft">{v}</span>
        </li>
      ))}
    </ol>
  </div>
);
